import { TotalResults } from '@shared/components/ui/TotalResults';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { isMobile } from 'react-device-detect';
import { useImportHistory } from '../../hooks/useImportHistory';
import { useImportHistoryColumns } from '../../hooks/useImportHistoryColumns';
import { useImportHistorySelection } from '../../hooks/useImportHistorySelection';
import { useToast } from '@shared/hooks/useToast';
import { ErrorState, LoadingState } from '@modules/Shops/List/components/feedback';
import { FiltersSection } from '../interaction/FiltersSection';
import { ImportHistoryDetailModal, ImportHistoryDetailModalRef } from '../interaction/ImportHistoryDetailModal';
import { ImportHistorySelectionToolbar } from '../interaction/ImportHistorySelectionToolbar';
import { ImportHistoryCardList } from '../display';
import { ImportHistoryItem } from '../../models/ImportHistoryModel';
import { ImportHistoryApiAdapter } from '../../adapters/ImportHistoryApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';

export const Main = () => {
  const { t } = useTranslation('shopsModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { shopId: urlShopId } = useParams<{ shopId: string }>();
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' }>(() => ({
    field: queryParams.sortBy ?? 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') ?? 'desc',
  }));

  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    setSorting((prev) => {
      const next = {
        field: queryParams.sortBy ?? 'id',
        direction: (queryParams.sortDirection as 'asc' | 'desc') ?? 'desc',
      };
      return prev.field === next.field && prev.direction === next.direction ? prev : next;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // 🎯 Filtros efectivos: si viene shopId en la URL (desde lista de tiendas), filtrar por esa tienda
  const effectiveFilterConditions = useMemo((): FilterConditionOutput[] => {
    const shopIdFilter: any[] =
      urlShopId && String(urlShopId).trim()
        ? [
            {
              column: 'shop_id',
              action: 'in',
              value: [String(urlShopId).trim()],
            },
          ]
        : [];
    return [...shopIdFilter, ...filterConditions];
  }, [urlShopId, filterConditions]);

  // 🎯 Procesar filtros para usar advanced_filters
  const processedFilters = useMemo(() => {
    const filters: any = {};

    if (effectiveFilterConditions.length > 0) {
      const advancedFilters = {
        filters: effectiveFilterConditions.map((condition) => ({
          column: condition.column,
          operator: condition.action,
          value: condition.value,
        })),
        logic: 'AND' as const,
      };
      filters.advanced_filters = advancedFilters;
    }

    return filters;
  }, [effectiveFilterConditions]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      updateQueryParams({ filters: conditions });
    },
    [updateQueryParams],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setSearchTerm(searchTerm);
      updateQueryParams({ search: searchTerm });
    },
    [updateQueryParams],
  );

  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      const next = newSorting ?? { field: 'id', direction: 'desc' as const };
      setSorting(next);
      updateQueryParams({ sortBy: next.field, sortDirection: next.direction });
    },
    [updateQueryParams],
  );

  const { items, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useImportHistory({
      per_page: 50, // Aumentamos para mejor visualización
      searchTerm,
      filterConditions: effectiveFilterConditions,
      sort_column: sorting.field,
      sort_direction: sorting.direction,
    });

  const apiAdapter = useMemo(() => new ImportHistoryApiAdapter(createHttpClient(apiClientWH)), []);
  const detailModalRef = useRef<ImportHistoryDetailModalRef>(null);
  const gridRef = useRef<AgGridTableRef>(null);

  // 🎯 Manejo de selección
  const { selectedRows, handleSelectionChanged, setSelectedRows } = useImportHistorySelection<ImportHistoryItem>();

  const retryableCount = useMemo(
    () => selectedRows.filter((row: ImportHistoryItem) => row.status === 'failed').length,
    [selectedRows],
  );

  // 🎯 Handlers
  const handleViewDetails = useCallback((item: ImportHistoryItem) => {
    detailModalRef.current?.open(item);
  }, []);

  const handleRetry = useCallback(
    async (item: ImportHistoryItem) => {
      const res = await apiAdapter.retryImport(item.id);
      if (res.success) {
        toast({
          title: t('common.success', 'Éxito'),
          description: res.message,
          variant: 'default',
        });
        refetch();
      } else {
        toast({
          title: t('common.error', 'Error'),
          description: res.message,
          variant: 'destructive',
        });
      }
    },
    [apiAdapter, refetch, t, toast],
  );

  const handleRetryBatch = useCallback(async () => {
    const ids = selectedRows
      .filter((row: ImportHistoryItem) => row.status === 'failed')
      .map((row: ImportHistoryItem) => row.id);
    if (ids.length === 0) return;

    const res = await apiAdapter.retryImportBatch(ids);
    if (res.success) {
      toast({
        title: t('common.success', 'Éxito'),
        description: res.message,
        variant: 'default',
      });
      setSelectedRows([]);
      refetch();
    } else {
      toast({
        title: t('common.error', 'Error'),
        description: res.message,
        variant: 'destructive',
      });
    }
  }, [apiAdapter, selectedRows, refetch, setSelectedRows, t, toast]);

  const columnDefs = useImportHistoryColumns({
    onViewDetails: handleViewDetails,
    onRetry: handleRetry,
  });

  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.tiendas', 'Tiendas'), path: '/shops' },
      { label: t('breadcrumb.importHistory', 'Historial de importaciones') },
    ],
    [t],
  );

  const headerActions = useMemo(
    () => [
      {
        id: 'back',
        label: t('importHistory.back', 'Volver'),
        onClick: () => navigate('/shops'),
        variant: 'outline' as const,
        icon: <FontAwesomeIcon icon={faChevronLeft} className="!w-3.5 !h-3.5" />,
      },
    ],
    [navigate, t],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const hasActiveFilters = useMemo(
    () => searchTerm.length > 0 || filterConditions.length > 0 || Boolean(urlShopId && String(urlShopId).trim()),
    [searchTerm, filterConditions, urlShopId],
  );

  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('importHistory.loading', 'Cargando histórico...')} />;
  }

  if (error) {
    return <ErrorState error={error instanceof Error ? error : new Error(String(error))} onRetry={() => refetch()} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex sm:items-center sm:justify-between flex-wrap gap-3 mb-4 flex-col md:flex-row">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              filterConditions={filterConditions}
              filters={{
                ...processedFilters,
                searchTerm,
                filterConditions,
                sort_column: sorting.field,
                sort_direction: sorting.direction,
                per_page: 15,
              }}
            />
          </div>
          <div>
            <TotalResults total={total} />
          </div>
        </div>
      </div>

      {/* Vista de histórico: siempre en el DOM, controlado por opacity */}
      <div
        className={`mb-4 transition-opacity duration-200 ${
          items.length > 0 ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        } ${
          // Durante búsquedas (no primera carga): opacity-50
          !isInitialLoad && isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        {isMobile ? (
          <ImportHistoryCardList
            items={items}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            total={total}
            onViewDetails={handleViewDetails}
            onRetry={handleRetry}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
        ) : (
          <AgGridTable
            ref={gridRef}
            data={items}
            columnDefs={columnDefs}
            height="calc(100vh - 150px)"
            enableSelection={true}
            onSelectionChanged={handleSelectionChanged}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            className="rounded-lg shadow-sm"
          />
        )}
      </div>

      <ImportHistorySelectionToolbar
        selectedCount={selectedRows.length}
        retryableCount={retryableCount}
        onClose={() => {
          setSelectedRows([]);
          gridRef.current?.deselectAll();
        }}
        onRetrySelected={handleRetryBatch}
      />

      <ImportHistoryDetailModal ref={detailModalRef} onRetry={handleRetry} />

      {/* Estado vacío: solo cuando no hay datos Y no es carga inicial */}
      {!isInitialLoad && items.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.SHOPS}
          customAction={null}
        />
      )}
    </div>
  );
};
